import { useMemo } from "react";
import { useQuery } from "react-query";
import axios from "../config/axiosInstance";
import { MENU } from "../config/apiConfig";
import {
  DEFAULT_MENU,
  FIXED_MENU_LINKS,
  getFixedMenuItems,
} from "../config/menuCatalog";

/**
 * Aplica o layout montado por um representante em cima do catálogo padrão.
 *
 * ── A regra que sustenta a segurança disto ──────────────────────────────────
 * O layout guarda só ordem, agrupamento e rótulo. `permission` e `typeCompany`
 * saem SEMPRE do catálogo (src/config/menuCatalog.js), nunca do que veio da
 * API. Um link interno que não exista no catálogo é descartado — então
 * inventar `/admin/queries` no layout não abre a tela: o item some.
 *
 * O backend valida o mesmo conjunto ao gravar (menuService.CATALOG_LINKS). As
 * duas pontas falham fechado, e é de propósito que a checagem que decide a
 * renderização seja esta, do lado que de fato desenha o menu.
 */

/**
 * Índice link -> nó do catálogo, incluindo os filhos de cada submenu.
 *
 * A subárvore fixa ("Administrativo") fica de fora: ela é reinserida na posição
 * original, então um layout que a referenciasse produziria o item duplicado —
 * e, pior, numa posição escolhida por quem montou o layout.
 */
const buildCatalogIndex = (catalog) => {
  const index = new Map();

  const walk = (items) => {
    for (const item of items || []) {
      if (FIXED_MENU_LINKS.has(item?.link)) continue;
      if (item?.link) index.set(item.link, item);
      if (item?.children?.length) walk(item.children);
    }
  };

  walk(catalog);
  return index;
};

/** Junta as permissões dos filhos: um grupo é visível para quem vê algo dentro. */
const unionOf = (children, field, fallback) => {
  const values = new Set();
  for (const child of children) {
    for (const value of child?.[field] || []) values.add(value);
  }
  return values.size ? Array.from(values) : fallback;
};

const resolveLeaf = (entry, index) => {
  if (!entry || typeof entry !== "object") return null;

  if (entry.type === "external") {
    if (!entry.url || !entry.name) return null;
    // Link externo não tem tela nossa por trás, então não há o que recortar:
    // vale para qualquer usuário que enxergue o menu.
    return {
      name: entry.name,
      link: entry.url,
      isExternal: true,
      icon: entry.icon || "open_in_new",
      permission: ["USER"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    };
  }

  const catalogItem = index.get(entry.link);
  if (!catalogItem) return null;

  return {
    ...catalogItem,
    name: entry.name || catalogItem.name,
    icon: entry.icon || catalogItem.icon,
    // Um item do catálogo que era submenu vira folha quando o representante o
    // coloca dentro de um grupo; os filhos dele seguem disponíveis como itens
    // avulsos no editor.
    children: undefined,
    isCollapsible: false,
  };
};

export const applyMenuLayout = (catalog, layoutItems) => {
  if (!Array.isArray(layoutItems) || !layoutItems.length) return catalog;

  const index = buildCatalogIndex(catalog);
  const out = [];

  for (const entry of layoutItems) {
    if (entry?.type === "group") {
      const children = (entry.children || [])
        .map((child) => resolveLeaf(child, index))
        .filter(Boolean);

      if (!children.length) continue;

      out.push({
        name: entry.name,
        link: `#grupo-${out.length}`,
        icon: entry.icon || "folder",
        isCollapsible: true,
        permission: unionOf(children, "permission", ["USER"]),
        typeCompany: unionOf(children, "typeCompany", ["ALL"]),
        children,
      });
      continue;
    }

    const leaf = resolveLeaf(entry, index);
    if (leaf) out.push(leaf);
  }

  // Layout que não resolveu nada (catálogo mudou, links removidos) cairia num
  // menu vazio e deixaria o usuário sem navegação: melhor voltar ao padrão.
  if (!out.length) return catalog;

  /* "Administrativo" não passa pelo layout: volta na posição que ocupa no
     catálogo. Como é o último item, na prática vai para o fim — mas usar o
     índice mantém o comportamento correto se um dia ele mudar de lugar ou
     surgir um segundo item fixo. */
  for (const { item, index: position } of getFixedMenuItems(catalog)) {
    out.splice(Math.min(position, out.length), 0, item);
  }

  return out;
};

/**
 * Menu efetivo do usuário logado.
 *
 * Devolve `{ menuLinks, isLoading }`. Enquanto a resposta não chega, menuLinks
 * já é o catálogo padrão — mas quem consome deve usar `isLoading` para mostrar
 * um esqueleto: renderizar o padrão e reorganizá-lo meio segundo depois faz o
 * menu saltar sob o cursor de quem já estava clicando.
 *
 * Com a query desabilitada (perfil SELLER, usuário ainda não carregado) não há
 * nada em voo, então `isLoading` é falso — senão o menu do vendedor, que nem
 * usa layout, ficaria preso no esqueleto para sempre.
 */
const useMenuLayout = (enabled = true) => {
  const { data: layout, isLoading } = useQuery(
    ["menu-layout"],
    async () => {
      const { data } = await axios.get(MENU.GET_CURRENT);
      return data?.data ?? null;
    },
    {
      enabled: !!enabled,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      // Menu é navegação: falhar a busca do layout não pode tirar o usuário do
      // ar. Sem dado, o Sidebar segue com o catálogo padrão.
      retry: false,
      onError: () => {},
    },
  );

  const menuLinks = useMemo(
    () => applyMenuLayout(DEFAULT_MENU, layout?.items),
    [layout],
  );

  return { menuLinks, isLoading: !!enabled && isLoading };
};

export default useMenuLayout;
